import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/apiFetch";
import type { Drug, DrugCoverageEntry, DrugCoverageResponse } from "../types/api";

const MIN_QUERY = 3;
const DEBOUNCE_MS = 300;

/** One search hit: the catalog row plus its plan coverage (null = no plan / not resolved). */
export interface DrugSearchResult {
  drug: Drug;
  coverage: DrugCoverageEntry | null;
}

export interface UseDrugSearchReturn {
  /** True once the trimmed query reaches `MIN_QUERY` characters; search takes over the list. */
  active: boolean;
  /** True while the debounce is pending or a lookup is in flight. */
  loading: boolean;
  /** The search request itself failed (coverage failures degrade silently instead). */
  error: boolean;
  results: DrugSearchResult[];
}

/**
 * Debounced server-side drug search against the public `GET /api/drugs?search=`,
 * behind the customer medication picker and the member portal's add bar. The
 * search activates at `MIN_QUERY` characters and the server handles case. The
 * server caps the rows at `SEARCH_LIMIT` (server/app/api/drugs.py), so the
 * full catalog never loads client-side. `baseUrl` is the SPA's
 * `VITE_API_BASE_URL` (see `apiFetch`).
 *
 * Once results land and a catalogue `planId` exists, coverage resolves in the
 * same pass through `GET /api/plans/{planId}/drug-coverage?rxcuis=…`, under
 * `planYear`'s formulary when one is given (the toggle's year; null sends no
 * param, the current year). A coverage failure or a null plan leaves every
 * entry's coverage null, and the results still render. A sequence guard drops
 * stale responses, as in `useAddressAutocomplete`.
 */
export function useDrugSearch(
  query: string,
  planId: string | null,
  baseUrl: string,
  planYear: number | null = null,
): UseDrugSearchReturn {
  const trimmed = query.trim();
  const active = trimmed.length >= MIN_QUERY;
  const [state, setState] = useState<Omit<UseDrugSearchReturn, "active">>({
    loading: false,
    error: false,
    results: [],
  });
  const seqRef = useRef(0);

  useEffect(() => {
    const seq = ++seqRef.current;
    if (!active) {
      setState({ loading: false, error: false, results: [] });
      return;
    }
    setState({ loading: true, error: false, results: [] });

    const run = async (): Promise<void> => {
      try {
        const drugs = await apiFetch<Drug[]>(
          `/api/drugs?search=${encodeURIComponent(trimmed)}`,
          {},
          { baseUrl },
        );
        let coverage = new Map<string, DrugCoverageEntry>();
        if (planId !== null && drugs.length > 0) {
          try {
            const year = planYear === null ? "" : `&planYear=${planYear}`;
            const resp = await apiFetch<DrugCoverageResponse>(
              `/api/plans/${encodeURIComponent(planId)}/drug-coverage?rxcuis=${drugs
                .map((d) => d.rxcui)
                .join(",")}${year}`,
              {},
              { baseUrl },
            );
            coverage = new Map(resp.drugs.map((e) => [e.rxcui, e]));
          } catch {
            // Coverage is optional; the results still render without it.
          }
        }
        if (seq !== seqRef.current) return; // stale: a newer query won
        setState({
          loading: false,
          error: false,
          results: drugs.map((d) => ({ drug: d, coverage: coverage.get(d.rxcui) ?? null })),
        });
      } catch {
        if (seq !== seqRef.current) return;
        setState({ loading: false, error: true, results: [] });
      }
    };

    const timer = window.setTimeout(() => void run(), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [active, trimmed, planId, baseUrl, planYear]);

  return { active, ...state };
}
